// Cloud sync decisions, pure. Each store is one document; the newest updatedAt wins, whole
// documents are replaced. Joining an account is different: the first sync of a device takes the
// account's copy of everything (a fresh device's few launch-time settings must not overwrite the
// account's), except the library, which is merged item by item so no recipe is lost.

export type Stamp = { name: string; updatedAt: number };

/**
 * What to pull (server newer) and what to push (device newer, or not on the server yet).
 * `joining`: this device has never synced with this account, so the account's copy wins.
 */
export function plan(local: Stamp[], remote: Stamp[], joining = false): { pull: string[]; push: string[] } {
  const server = new Map(remote.map(r => [r.name, r.updatedAt]));
  const pull: string[] = [];
  const push: string[] = [];
  for (const { name, updatedAt } of local) {
    const theirs = server.get(name);
    if (theirs !== undefined && (joining || theirs > updatedAt)) pull.push(name);
    else if (updatedAt > 0 && (theirs === undefined || updatedAt > theirs)) push.push(name);
  }
  return { pull, push };
}

type Item = { id: string; updatedAt?: number };
type Library = Record<string, unknown> & { updatedAt: number; hiddenBuiltIns?: string[]; dietConfirmations?: Record<string, unknown> };

/**
 * Both libraries, item by item: the newer copy of each item, everything only one side has, hidden
 * built-ins of both. Lists without per-item dates (diet confirmations) come from the newer library.
 */
export function mergeLibraries<T extends Library>(mine: T, theirs: T, kinds: readonly string[], now: number): T {
  const [newer, older] = mine.updatedAt >= theirs.updatedAt ? [mine, theirs] : [theirs, mine];
  const out: Library = { ...newer, updatedAt: now };
  for (const kind of kinds) {
    const a = (older[kind] ?? {}) as Record<string, Item>;
    const b = (newer[kind] ?? {}) as Record<string, Item>;
    const merged: Record<string, Item> = { ...a };
    for (const [id, item] of Object.entries(b)) if (!merged[id] || (item.updatedAt ?? 0) >= (merged[id].updatedAt ?? 0)) merged[id] = item;
    out[kind] = merged;
  }
  out.hiddenBuiltIns = [...new Set([...(older.hiddenBuiltIns ?? []), ...(newer.hiddenBuiltIns ?? [])])];
  return out as T;
}

/** Wait after the last change before pushing, so a burst of edits is one upload. */
export const PUSH_DELAY_MS = 4000;

// ---- Merging two accounts (a Telegram account that had its own data joins a web account).
// The server parks the joining account's copy of each shared document as "merge-<name>"; the app
// merges it into its own: every item kept (items carry ids), and where both have the same item or
// setting, the side the person chose wins. Settings are taken whole from the winner.

export const MERGE_PREFIX = 'merge-';
export const MERGE_WINNER = 'merge-winner';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
/** How list items are told apart: their id, or for pantry items kind + referenced id. */
const keyOf = (v: unknown): string | null => (isObj(v) ? (typeof v.id === 'string' ? v.id : typeof v.refId === 'string' ? `${String(v.kind)}:${v.refId}` : null) : null);
const isIdMap = (o: Obj) => Object.entries(o).every(([k, v]) => isObj(v) && v.id === k);

/** `winner` and `loser` merged: lists and id-maps keep every item (the winner's copy on a clash), everything else is the winner's. */
export function mergeValues(winner: unknown, loser: unknown): unknown {
  if (winner === undefined) return loser;
  if (Array.isArray(winner) && Array.isArray(loser)) {
    const all = [...winner, ...loser];
    if (all.length && all.every(v => keyOf(v) !== null)) {
      const seen = new Set(winner.map(keyOf));
      return [...winner, ...loser.filter(v => !seen.has(keyOf(v)))];
    }
    if (all.every(v => !isObj(v) && !Array.isArray(v))) return [...new Set(all)];
    return winner;
  }
  if (isObj(winner) && isObj(loser)) {
    if (isIdMap(winner) && isIdMap(loser) && Object.keys(winner).length + Object.keys(loser).length > 0) return { ...loser, ...winner };
    const out: Obj = { ...loser };
    for (const [k, v] of Object.entries(winner)) out[k] = k in loser ? mergeValues(v, loser[k]) : v;
    return out;
  }
  return winner;
}

/** One document of each account, merged; `incomingWins` when the joining account's side was chosen. */
export function mergeDocs(name: string, mine: Obj, incoming: Obj, incomingWins: boolean, now: number): Obj {
  const [winner, loser] = incomingWins ? [incoming, mine] : [mine, incoming];
  const merged = name === 'settings' ? { ...winner } : (mergeValues(winner, loser) as Obj);
  return { ...merged, updatedAt: now };
}
